import { useState } from 'react';
import { Container, Row, Col, Button, Alert, Spinner } from 'reactstrap';
import { Link, useNavigate } from 'react-router-dom';

import { Formik, Form } from 'formik';
import { FormikInput as Input, useTranslator, useToast } from '@front-activerse/common-components';
import validationSchema from './validationSchema';

const AuthRemindPasswordComponent = ({ remindPassword, isLoggedIn, loading }) => {
  const { t } = useTranslator();
  const { toastError } = useToast();
  const navigate = useNavigate();

  const [success, setSuccess] = useState(false);
  const [errors, setErrors] = useState([]);
  const [userEmail, setUserEmail] = useState('');

  const initialValues = {
    email: '',
  };

  const handleSubmit = async (values, { resetForm }) => {
    setErrors([]);
    try {
      const response = await remindPassword({ email: values.email });
      const { code, details } = response.data.auth.remindPassword;

      if (code === 200) {
        setUserEmail(values.email);
        setSuccess(true);
        resetForm();
      } else if (code === 422) {
        const errorsArray = [];
        for (let i = 0; i < details.length; i += 1) {
          const error = details[i];
          if (error.fieldName === 'email') {
            error.messages.forEach((message) => {
              errorsArray.push(message);
            });
          }
        }
        setErrors(errorsArray);
      }
    } catch (e) {
      toastError();
      throw new Error(`Failed to remind password, got error: ${e}`);
    }
  };

  if (isLoggedIn) {
    navigate('/');
  }

  return (
    <div className="page-template-auth full-height7">
      <Container className="py-xxl">
        <Row>
          <Col
            xs={12}
            md={{ size: 8, offset: 2 }}
            xl={{ size: 6, offset: 3 }}
            xxl={{ size: 4, offset: 4 }}
          >
            <h1 className="text-center title-medium semi-bold mb-xl">{t('resetPassword.title')}</h1>
            <div className="page-container">
              <div className="auth-container">
                {success && (
                  <Alert color="info">
                    <p className="mb-0">
                      {t('resetPassword.instructionsSent.notification', { userEmail })}
                    </p>
                  </Alert>
                )}
                <p className="text-text-variant-color">
                  {t('resetPassword.enterEmailForPasswordChangeInstructions')}
                </p>
                <Formik
                  validationSchema={validationSchema}
                  validateOnChange
                  initialValues={initialValues}
                  onSubmit={handleSubmit}
                >
                  {({ isValid }) => {
                    return (
                      <Form>
                        <Input
                          name="email"
                          type="email"
                          label={t('inputLabel.emailAddress')}
                          translateErrMsg
                        />
                        {errors.length > 0 && (
                          <p className="error my-xl text-center">
                            {errors.map((error, idx) => (
                              <span className="d-block" key={idx}>
                                {error}
                              </span>
                            ))}
                          </p>
                        )}
                        <div className="mt-xl">
                          <Button
                            type="submit"
                            color="primary"
                            className="w-100"
                            disabled={!isValid || loading}
                            data-test-id="reset_pass_send_password_button"
                          >
                            {loading ? (
                              <>
                                <Spinner size="sm" className="me-2">
                                  Loading...
                                </Spinner>
                                {t('resetPassword.sending.button')}
                              </>
                            ) : (
                              t('resetPassword.send.button')
                            )}
                          </Button>
                        </div>
                      </Form>
                    );
                  }}
                </Formik>
              </div>
            </div>

            <p className="mt-xl text-center">
              {t('resetPassword.backTo')}{' '}
              <Link to="/auth/login">{t('resetPassword.logIn.button')}</Link>
            </p>
          </Col>
        </Row>
      </Container>
    </div>
  );
};

export default AuthRemindPasswordComponent;
